import { Component, EventEmitter, Input, Output, ViewEncapsulation} from '@angular/core';
import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { FieldValue, FieldOption, RecordField, RecordSection } from '../cfi-record.model';
import { matchesCondition } from '../cfi-record-condition.util';
import { isLabelValued } from '../cfi-record-select.util';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'cfi-record-view',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [ReactiveFormsModule, MatIconModule],
  templateUrl: './cfi-record-view.html',
  styleUrls: ['./cfi-record-view.scss']
})
export class CfiRecordViewComponent {
  @Input({ required: true }) title!: string;
  @Input({ required: true }) sections!: RecordSection[];
  @Input({ required: true }) form!: FormGroup;
  @Input({ required: true }) options!: Record<string, FieldOption[]>;
  @Input() canEdit = false;
  @Input() columns = 3;
  @Input() headerMetaLabel: string | null = null;
  @Input() headerMetaValue: FieldValue = null;
  @Input() initiateTransferButtonText = 'Initiate school transfer';
  @Input() changeGuardianButtonText = 'Change Guardian';
  @Input() emptyStateButtonText = 'Apply';
  @Input() resendAwardOfferButtonText = 'Resend Award Offer';

  @Input() showInitiateTransferButton = false;
  @Input() showChangeGuardianButton = false;
  @Input() showResendAwardOfferButton = false;
  
  @Input() prefix = 'field';
  @Input() useGrayTheme = false;
  @Input() showEmptyState = false;
  /** Disables the empty-state Apply button (e.g. while the enrollment period is closed). */
  @Input() emptyStateButtonDisabled = false;
  @Input() printButtonText = 'Print';
  @Input() showPrintButton = false;

  @Output() edit = new EventEmitter<void>();
  @Output() initiateTransfer = new EventEmitter<void>();
  @Output() changeGuardian = new EventEmitter<void>();
  @Output() resendAwardOffer = new EventEmitter<void>();
  @Output() emptyStateApply = new EventEmitter<void>();
  @Output() print = new EventEmitter<void>();
  
  getValue(field: RecordField): FieldValue {
    return (this.form.get(field.key)?.value as FieldValue) ?? null;
  }

  isVisible(field: RecordField): boolean {
    if (field.editOnly) return false;
    if (!field.showWhen) return true;
    const { field: watchField, is } = field.showWhen;

    return matchesCondition(this.form.get(watchField)?.value as FieldValue, is);
  }

  getOptions(field: RecordField): FieldOption[] {
    if (field.type === 'boolean') return this.options['yesNo'] ?? [];
    if (!field.optionsKey) return [];
    return this.options[field.optionsKey] ?? [];
  }

  displayValue(field: RecordField): string {
    const value = this.getValue(field);

    if (field.type !== 'select' && field.type !== 'search-select' && (value == null || value === '')) return '—';

    switch (field.type) {
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'currency': {
        const digits = field.fractionDigits ?? 0;
        return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
      }
      case 'date':
        return new Date(String(value)).toLocaleDateString('en-US');
      case 'select':
      case 'search-select': {
        if (value == null) return '—';
        const options = this.getOptions(field);
        if (!options.length) return String(value);
        const matched = options.find((o) => o.value === value);
        if (matched) return matched.label;
        return isLabelValued(options) ? String(value) : '—';
      }
      default:
        return String(value);
    }
  }

  trackBySection(_: number, section: RecordSection): string {
    return section.title;
  }

  trackByField(_: number, field: RecordField): string {
    return field.key;
  }
  
}
